import type { SelectOption } from '@/components/Select';
import { Select } from '@/components/Select';
import type { Language } from '@/i18n/dictionary';
import { LANGUAGE } from '@/i18n/dictionary';
import { useTranslation } from '@/i18n/useTranslation';
import { useKioskSession } from '@/lib/kioskSession';

import { daysInBirthMonth, isDayInBirthMonth, MAX_BIRTH_DAY } from '../birthday';

export interface DateOfBirthFieldProps {
  readonly day: string;
  readonly month: string;
  readonly onDayChange: (value: string) => void;
  readonly onMonthChange: (value: string) => void;
  /** Draws the frame's required marker beside the group label. See `Field` for why it sits outside it. */
  readonly required?: boolean;
  /**
   * The group's validation message — **same contract as `Field` and `Select`: shown only once the candidate
   * has tried to continue.**
   *
   * The screen that owns the submit decides WHEN, because it is the only thing that knows a Continue was
   * pressed; this component decides where the sentence sits. It used to decide both, and that is the defect
   * this prop removes — see the reserved line below.
   */
  readonly error?: string;
}

/**
 * Month and day of birth — **one labelled group with two dropdowns** [Figma · 12021:40419].
 *
 * This is the correction that changes behaviour, not just appearance. KID-1 shipped two free-text numeric
 * inputs under separate "Day of birth" / "Month of birth" labels; the design draws a single
 * *"Date of birth (month & day)"* label over two selects. A dropdown cannot produce `32`, `00` or `abc`, so
 * an entire class of failed lookup — and with it a wasted throttle attempt — stops existing at the input.
 *
 * The year is neither offered nor collected [decision 0032].
 *
 * **The day list follows the month** [ISS-148]: April offers 30, February 29 — there is no year, so no leap
 * year to rule out [`../birthday.ts`]. Until then it offered 1–31 for every month and `31/02` was accepted.
 * A day the NEW month does not have is cleared when the month changes, never moved to the nearest one: the
 * form does not know which of the two the candidate got wrong, and guessing writes a birthday that is not
 * theirs into a lookup that must match it exactly. The empty Day is then the reserved line's to name on
 * Continue, like any other half left unchosen.
 *
 * **MONTH comes first, then DAY** [0301], and every source now agrees: the label *"Date of birth (month &
 * day)"*, the prototype's `lkDobMonth` then `lkDobDay`, and the frames. Re-read live 2026-09-30, all nine
 * check-in frames (`New user`, `Returning user` and `Email` × Desktop / iPad / Mobile) draw
 * `Select / DOB Month` on the left of the row and `Select / DOB Day` on its right. The design update the
 * 2026-09-15 read reported as owed has landed.
 */
export function DateOfBirthField({
  day,
  month,
  onDayChange,
  onMonthChange,
  required,
  error,
}: DateOfBirthFieldProps) {
  const t = useTranslation();
  const language = useKioskSession((state) => state.language);

  /*
   * `role="group"` + `aria-labelledby` rather than `<fieldset><legend>`.
   *
   * The semantics are identical — a screen reader still announces "Date of birth, Day" rather than a bare
   * "Day" — but a `<legend>` is laid out specially by the browser and does NOT take part in its fieldset's
   * flex `gap`. That silently collapsed this group to 60px against the frame's 68 and pushed every control
   * below it up by 8, which was four of the differences the structural diff reported.
   */
  return (
    <div aria-labelledby="check-in-dob-label" className="flex w-full flex-col gap-2" role="group">
      <div className="flex items-center gap-1">
        <p
          className="text-base font-semibold leading-5 text-[var(--foreground)]"
          id="check-in-dob-label"
        >
          {t('checkIn.dob')}
        </p>
        {required === true && (
          /*
            `leading-5`, like the other four required markers. Without it the span inherited the body's
            line-height and stretched this row to 24 against the frame's 20 — the one asterisk of the five
            that a previous pass missed, on the screen that pass named.
          */
          <span aria-hidden="true" className="text-base leading-5 text-[var(--destructive)]">
            *
          </span>
        )}
      </div>
      {/*
        Month, then day — the order the group's label states and the frames draw [0301]. `FACTOR_CONTROL_IDS`
        in `CheckInScreen` is the list that has to move with it: "focus the first gap" means the first one ON
        SCREEN.
      */}
      <div className="flex w-full items-start gap-3">
        <Select
          id="check-in-birthMonth"
          label={t('checkIn.dobMonth')}
          labelHidden
          onChange={(value) => {
            onMonthChange(value);
            if (!isDayInBirthMonth(day, value)) onDayChange('');
          }}
          options={MONTHS[language]}
          placeholder={t('checkIn.dobMonth')}
          value={month}
        />
        <Select
          id="check-in-birthDay"
          label={t('checkIn.dobDay')}
          labelHidden
          onChange={onDayChange}
          options={dayOptions(month)}
          placeholder={t('checkIn.dobDay')}
          value={day}
        />
      </div>
      {/*
        The message line is RESERVED, always: the frame draws a 20px `Validation message` slot under the
        row [12446:28586 / 28587 / 28588, all three viewports] whether or not anything is wrong. Rendering
        it conditionally moves the SSN field and the callout 20px down the instant a candidate picks one of
        the two dropdowns — the form jumps under the finger that just touched it.

        ⚠️ **It speaks on Continue, never on selection** — corrected 2026-09-14, reported from the running
        kiosk. It used to speak the moment exactly one of the two dropdowns was chosen, which tells a
        candidate they are wrong halfway through being right: picking a day and reaching for the month is
        the normal way to answer this, not a mistake. The prototype defers every message on this form to
        the Continue press and the application screen already works that way; check-in was the outlier.

        The region is PERMANENT and only its text changes — `role="status"` with `aria-live`, never a
        `role="alert"` added to an already-mounted node. A live region has to exist before the change it
        should announce: adding the role in the same commit that fills the text is inconsistently honoured,
        and VoiceOver on the iPad in particular says nothing. The reserved line makes the permanent form
        free, since the node is in the DOM either way. `status` rather than `alert` because this is a nudge
        about an unfinished field, not an error that interrupts.
      */}
      <p
        aria-live="polite"
        className="min-h-5 w-full text-xs font-semibold leading-5 text-[var(--error-strong)]"
        role="status"
      >
        {error ?? ''}
      </p>
    </div>
  );
}

/** Days as bare numbers — only as many as the chosen month has [ISS-148]. */
const DAYS = Array.from({ length: MAX_BIRTH_DAY }, (_, index) => {
  const value = String(index + 1);
  return { value, label: value };
});

function dayOptions(month: string): typeof DAYS {
  return DAYS.slice(0, daysInBirthMonth(month));
}

/**
 * The months by NAME, in the kiosk's language — and by NUMBER underneath [ISS-153].
 *
 * Heidi (ISS, UAT) asked for names rather than numbers. **Only the label changed**: each option's value is
 * still `1`–`12`, so the store, the lookup and the create send exactly what they sent before, and the
 * form still accepts a month 1–12 and nothing else [03-us-kid-2 § *Only day and month of birth are
 * collected*; decision 0032]. A name never travels to the api.
 *
 * The names come from `Intl` in the selected language, not from the dictionary — the precedent
 * `DateCalendar` set: twelve months in two languages are strings the platform already has right, including
 * Spanish writing them in lower case (`enero`), which the dictionary would have to restate by hand.
 */
function monthOptions(locale: string): readonly SelectOption[] {
  const name = new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' });
  return Array.from({ length: 12 }, (_, index) => ({
    value: String(index + 1),
    // Mid-month, not the 1st: a US time zone is behind UTC, so the 1st at midnight UTC is still the
    // previous month locally. `timeZone: 'UTC'` already prevents that; the 15th keeps it true without it.
    label: name.format(new Date(Date.UTC(2000, index, 15))),
  }));
}

const MONTHS: Readonly<Record<Language, readonly SelectOption[]>> = {
  [LANGUAGE.en]: monthOptions('en-US'),
  [LANGUAGE.es]: monthOptions('es'),
};
